<template>
  <div>
    <v-row class="ma-0 my-4 align-center justify-space-between">
      <h1 class="text-h5 font-weight-bold">Registro Individual de Billing</h1>
    </v-row>

    <v-alert v-if="success" type="success" dense outlined dismissible class="mb-3">
      Registro creado correctamente.
    </v-alert>
    <v-alert v-if="error" type="error" dense outlined dismissible class="mb-3">{{ error }}</v-alert>

    <v-card outlined class="pa-6" max-width="700">
      <v-form ref="form">
        <v-row>
          <!-- Contrato (primer campo) -->
          <v-col cols="12">
            <v-autocomplete
              v-model="form.contratoId"
              :items="contratos"
              item-text="codigoEmpresa"
              item-value="id"
              label="Contrato *"
              dense outlined
              :loading="loadingSelects"
              :rules="[v => !!v || 'Requerido']"
              hint="Selecciona el contrato — la empresa se deriva automáticamente"
              persistent-hint
              @change="onContratoChange"
            />
          </v-col>

          <!-- Empresa (derivada, read-only) -->
          <v-col v-if="empresaNombre" cols="12">
            <v-text-field
              :value="empresaNombre"
              label="Empresa"
              dense outlined
              readonly
              background-color="grey lighten-4"
              prepend-inner-icon="mdi-domain"
            />
          </v-col>

          <!-- Moneda -->
          <v-col cols="12" md="4">
            <v-select
              v-model="form.currency"
              :items="monedas"
              label="Moneda *"
              dense outlined
              :rules="[v => !!v || 'Requerido']"
            />
          </v-col>

          <!-- MRR Local -->
          <v-col cols="12" md="4">
            <v-text-field
              v-model.number="form.priceBilling"
              label="MRR Local *"
              type="number"
              :step="form.currency === 'CLP' ? 1 : 0.01"
              dense outlined
              :rules="[v => (v !== null && v !== '') || 'Requerido', v => v > 0 || 'Debe ser mayor a 0']"
            />
          </v-col>

          <!-- Año (primero) -->
          <v-col cols="12" md="4">
            <v-text-field
              v-model.number="form.yearBilling"
              label="Año *"
              type="number"
              dense outlined
              :rules="[v => !!v || 'Requerido', v => v >= 2020 || 'Año inválido']"
            />
          </v-col>

          <!-- Mes (después del año) -->
          <v-col cols="12" md="4">
            <v-select
              v-model.number="form.monthBilling"
              :items="mesesSelect"
              label="Mes *"
              dense outlined
              :rules="[v => !!v || 'Requerido']"
            />
          </v-col>

          <!-- Notas -->
          <v-col cols="12">
            <v-textarea
              v-model="form.notas"
              label="Notas"
              rows="3"
              dense outlined
              hint="Opcional"
              persistent-hint
            />
          </v-col>
        </v-row>

        <v-row class="mt-2">
          <v-col cols="12" class="d-flex justify-end">
            <v-btn text class="mr-2" @click="resetForm">Limpiar</v-btn>
            <v-btn color="primary" :loading="saving" @click="submit">
              <v-icon left>mdi-content-save</v-icon>Guardar registro
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-card>
  </div>
</template>

<script>
import BillingService from '../services/billing.service';
import ContratoService from '../services/contrato.service';

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;

const emptyForm = () => ({
  contratoId: null,
  companyId: null,
  priceBilling: null,
  currency: 'CLP',
  monthBilling: currentMonth,
  yearBilling: currentYear,
  notas: '',
});

export default {
  name: 'BillingIndividual',
  data() {
    return {
      saving: false,
      loadingSelects: false,
      success: false,
      error: null,
      form: emptyForm(),
      contratos: [],
      empresaNombre: '',
      monedas: ['CLP', 'USD', 'PEN', 'MXN'],
      mesesSelect: [
        { text: 'Enero', value: 1 }, { text: 'Febrero', value: 2 }, { text: 'Marzo', value: 3 },
        { text: 'Abril', value: 4 }, { text: 'Mayo', value: 5 }, { text: 'Junio', value: 6 },
        { text: 'Julio', value: 7 }, { text: 'Agosto', value: 8 }, { text: 'Septiembre', value: 9 },
        { text: 'Octubre', value: 10 }, { text: 'Noviembre', value: 11 }, { text: 'Diciembre', value: 12 },
      ],
    };
  },
  mounted() {
    this.loadSelects();
  },
  methods: {
    async loadSelects() {
      this.loadingSelects = true;
      const contRes = await ContratoService.getAll();
      if (contRes.status === 'ok') {
        // El contrato ya trae la empresa incluida (company.name) — no hace
        // falta cruzar contra una lista separada de empresas.
        this.contratos = (contRes.data || [])
          .map(c => ({
            ...c,
            codigoEmpresa: [
              c.codigo,
              c.company?.name || 'Sin empresa',
              c.producto?.nombre,
            ].filter(Boolean).join(' — '),
          }))
          .sort((a, b) => (a.codigo || '').localeCompare(b.codigo || '', undefined, { numeric: true }));
      }
      this.loadingSelects = false;
    },

    onContratoChange(contratoId) {
      const contrato = this.contratos.find(c => c.id === contratoId);
      if (contrato) {
        this.form.companyId = contrato.companyId;
        this.empresaNombre = contrato.company?.name || '';
      } else {
        this.form.companyId = null;
        this.empresaNombre = '';
      }
    },

    async submit() {
      if (!this.$refs.form.validate()) return;
      this.saving = true;
      this.error = null;
      this.success = false;

      const payload = {
        companyId:    this.form.companyId,
        contratoId:   this.form.contratoId,
        priceBilling: this.form.priceBilling,
        currency:     this.form.currency,
        monthBilling: this.form.monthBilling,
        yearBilling:  this.form.yearBilling,
      };
      if (this.form.notas) payload.notas = this.form.notas;

      const res = await BillingService.createIndividual(payload);
      if (res.status === 'ok') {
        this.success = true;
        this.resetForm();
      } else {
        this.error = 'No se pudo guardar el registro. Revisa los datos e intenta de nuevo.';
      }
      this.saving = false;
    },

    resetForm() {
      this.form = emptyForm();
      this.empresaNombre = '';
      this.$refs.form?.resetValidation();
    },
  },
};
</script>
